import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faArrowUpFromBracket, faMagnifyingGlass, faXmark, faFileLines, faTable, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Button, FormControl, InputLabel, Select, MenuItem, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import KnowledgeLimitInfoModal from './knowledgeLimitInfoModal.js';
import ConfirmImportModal from './ConfirmImportModal.js';
import { resolveSelection } from '../utils/resolveSelection.js';
import NotionLogo from './NotionLogo.js';
import './NotionPickerModal.css';

import ManageConnectionsModal from './ManageConnectionsModal.js';
const API_URL = process.env.REACT_APP_API_BASE_URL;

function NotionPickerModal({ isOpen, onClose, onFilesSelected, botId, remainingSlots = Infinity, onUpgrade, returnTo = 'knowledge' }) {
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  // 'Manage accounts' -> the shared ManageConnectionsModal (rename/delete a
  // connected account). Re-added 2026-08-18: the old Connect page that hosted it
  // is gone, and the picker is where accounts are chosen, so this is where
  // customers look for it.
  const [manageOpen, setManageOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolvedFiles, setResolvedFiles] = useState([]);
  const [resolveError, setResolveError] = useState(false);

  const oauthPopupRef = useRef(null);
  const searchDebounceRef = useRef(null);

  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  };

  // Fetch the user's Notion connections (shared across the org)
  const fetchConnections = useCallback(async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const response = await fetch(`${API_URL}data-connections?user_id=${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const notionConnections = (data.connections || []).filter(
          c => c.platform === 'notion' && c.status === 'active'
        );
        setConnections(notionConnections);

        if (notionConnections.length === 1) {
          setSelectedConnection(notionConnections[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data connections:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setItems([]);
      setSelectedItems([]);
      setSearch('');
      setDebouncedSearch('');
      setNextCursor(null);
      fetchConnections();
    }
  }, [isOpen, fetchConnections]);

  // Debounce the search box
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search]);

  // Fetch pages/databases from Notion (server-side search)
  const fetchItems = useCallback(async (query, cursor = null, append = false) => {
    if (!selectedConnection) return;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoadingItems(true);
    }

    try {
      const params = new URLSearchParams();
      if (query) params.set('query', query);
      if (cursor) params.set('cursor', cursor);
      const qs = params.toString();
      const url = `${API_URL}data-connections/${selectedConnection.id}/notion-pages${qs ? `?${qs}` : ''}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setItems(prev => append ? [...prev, ...(data.files || [])] : (data.files || []));
        setNextCursor(data.next_page_token || null);
      } else {
        console.error('Failed to fetch Notion pages');
        if (!append) setItems([]);
      }
    } catch (error) {
      console.error('Error fetching Notion pages:', error);
      if (!append) setItems([]);
    } finally {
      setLoadingItems(false);
      setLoadingMore(false);
    }
  }, [selectedConnection]);

  // (Re)load the first page whenever the connection or debounced search changes
  useEffect(() => {
    if (selectedConnection) {
      setSelectedItems([]);
      fetchItems(debouncedSearch, null, false);
    }
  }, [selectedConnection, debouncedSearch, fetchItems]);

  const handleItemToggle = (item) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(f => f.id === item.id);
      if (isSelected) {
        return prev.filter(f => f.id !== item.id);
      }
      return [...prev, item];
    });
  };

  const doImport = async (itemsToImport) => {
    if (!itemsToImport.length || !selectedConnection) return;
    setImporting(true);
    try {
      const response = await fetch(
        `${API_URL}data-connections/${selectedConnection.id}/notion-import-pages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            bot_id: botId,
            files: itemsToImport,
          }),
        }
      );

      if (!response.ok) {
        // 403s are handled globally by the permission-denied modal —
        // close this picker quietly instead of showing a generic alert.
        if (response.status === 403) {
          onClose();
          return;
        }
        throw new Error('Failed to import pages');
      }

      const result = await response.json();

      if (onFilesSelected) {
        onFilesSelected(result.knowledge_ids, itemsToImport);
      }

      onClose();
    } catch (error) {
      console.error('Error importing Notion pages:', error);
      alert('Failed to import pages. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const importItems = async () => {
    if (!selectedItems.length || !selectedConnection) return;

    // If a database is selected, resolve its pages on the backend and show a
    // confirmation list (with a plan-limit check) before importing.
    if (selectedItems.some(it => it.objectType === 'database')) {
      setResolvedFiles([]);
      setResolveError(false);
      setConfirmOpen(true);
      setResolving(true);
      try {
        const files = await resolveSelection(selectedConnection.id, selectedItems);
        setResolvedFiles(files);
      } catch (error) {
        console.error('Error resolving database selection:', error);
        setResolvedFiles([]);
        setResolveError(true);
      } finally {
        setResolving(false);
      }
      return;
    }

    // Single pages only — enforce the limit and import directly.
    if (selectedItems.length > remainingSlots) {
      setLimitModalOpen(true);
      return;
    }
    await doImport(selectedItems);
  };

  const handleConnectNotion = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const response = await fetch(
        `${API_URL}data-connections/notion/connect?user_id=${userId}&return_to=${encodeURIComponent(returnTo)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('pendingKnowledgeTab', 'notion');
        localStorage.setItem('pendingOpenPicker', 'notion');

        if (isInIframe()) {
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          oauthPopupRef.current = window.open(
            data.authorization_url,
            'notion-oauth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          const checkPopup = setInterval(() => {
            if (oauthPopupRef.current?.closed) {
              clearInterval(checkPopup);
              oauthPopupRef.current = null;
              fetchConnections();
              // The user may have changed which pages are shared — refresh
              // the page list so newly granted pages show up immediately.
              if (selectedConnection) {
                fetchItems(debouncedSearch, null, false);
              }
            }
          }, 1000);
        } else {
          window.location.href = data.authorization_url;
        }
      }
    } catch (error) {
      console.error('Failed to connect Notion:', error);
      alert('Failed to connect Notion');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="notion-modal-overlay" onClick={onClose}>
      <div className="notion-modal-content" onClick={e => e.stopPropagation()}>
        <div className="notion-modal-header">
          <h2>
            <NotionLogo size={24} style={{ marginRight: '10px' }} />
            {t('notion_import_title') || 'Import from Notion'}
          </h2>
          <button className="notion-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="notion-modal-body">
          {loading ? (
            <div className="notion-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>{t('loading')}</p>
            </div>
          ) : connections.length === 0 ? (
            <div className="notion-no-connections">
              <NotionLogo size={64} style={{ marginBottom: '0px', opacity: 0.5 }} />
              <p>{t('notion_no_account_connected') || 'No Notion workspace connected'}</p>
              <p className="notion-hint">
                {t('notion_connect_hint') || 'Connect your Notion workspace to import pages.'}
              </p>
              <Button
                onClick={handleConnectNotion}
                variant="contained"
                sx={{
                  backgroundColor: '#5616ea',
                  '&:hover': { backgroundColor: '#4a14d6' },
                }}
              >
                {t('connect_notion') || 'Connect Notion'}
              </Button>
            </div>
          ) : (
            <>
              <div className="notion-account-selector">
                <FormControl
                  fullWidth
                  sx={{
                    '& label.Mui-focused': { color: '#5616ea' },
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': { border: '1px solid #515151' },
                    },
                  }}
                >
                  <InputLabel id="notion-account-select-label">
                    {t('notion_select_account') || 'Select Notion Workspace'}
                  </InputLabel>
                  <Select
                    labelId="notion-account-select-label"
                    value={selectedConnection?.id || ''}
                    onChange={e => {
                      const conn = connections.find(c => c.id === parseInt(e.target.value));
                      setSelectedConnection(conn);
                      setSelectedItems([]);
                      setSearch('');
                    }}
                    label={t('notion_select_account') || 'Select Notion Workspace'}
                  >
                    {connections.map(conn => (
                      <MenuItem key={conn.id} value={conn.id}>
                        {conn.display_name || conn.external_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <span className="notion-add-account-link" onClick={handleConnectNotion}>
                  {t('add_another_account') || 'Add another account'}
                </span>
                {connections.length > 0 && (

                  <span className="notion-add-account-link" style={{ marginLeft: '16px' }} onClick={() => setManageOpen(true)}>

                    {t('manage_accounts', 'Manage accounts')}

                  </span>

                )}
              </div>

              {selectedConnection && (
                <div className="notion-file-browser">
                  <div className="notion-search-row">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="notion-search-icon" />
                    <input
                      type="text"
                      className="notion-search-input"
                      placeholder={t('notion_search_placeholder') || 'Search pages and databases...'}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                      <button
                        type="button"
                        className="notion-search-clear"
                        onClick={() => setSearch('')}
                        aria-label="Clear search"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>

                  <div className="notion-reselect-row">
                    <span className="notion-reselect-hint">
                      {t('notion_missing_pages') || "Don't see a page?"}
                    </span>
                    <button type="button" className="notion-reselect-btn" onClick={handleConnectNotion}>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginRight: '6px' }} />
                      {t('notion_select_pages') || 'Select pages in Notion'}
                    </button>
                  </div>

                  {loadingItems ? (
                    <div className="notion-loading-files">
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>{t('loading')}</span>
                    </div>
                  ) : items.length === 0 ? (
                    <div className="notion-empty">
                      <p>{t('notion_no_pages') || 'No pages found'}</p>
                      <p className="notion-hint">
                        {t('notion_share_hint') || 'Only pages you shared with the integration appear here.'}
                      </p>
                    </div>
                  ) : (
                    <div className="notion-file-list">
                      {items.map(item => (
                        <div
                          key={item.id}
                          className={`notion-file-item ${selectedItems.some(f => f.id === item.id) ? 'selected' : ''}`}
                          onClick={() => handleItemToggle(item)}
                        >
                          <Checkbox
                            checked={selectedItems.some(f => f.id === item.id)}
                            onChange={() => handleItemToggle(item)}
                            onClick={e => e.stopPropagation()}
                            sx={{
                              color: '#5616ea',
                              '&.Mui-checked': { color: '#5616ea' },
                              padding: '4px',
                            }}
                          />
                          <FontAwesomeIcon
                            icon={item.objectType === 'database' ? faTable : faFileLines}
                            className="notion-file-icon"
                          />
                          <span className="notion-file-name">{item.name}</span>
                          {item.objectType === 'database' && (
                            <span className="notion-item-type-badge">{t('notion_database') || 'Database'}</span>
                          )}
                        </div>
                      ))}

                      {nextCursor && (
                        <button
                          className="notion-load-more"
                          onClick={() => fetchItems(debouncedSearch, nextCursor, true)}
                          disabled={loadingMore}
                        >
                          {loadingMore ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            t('load_more') || 'Load more'
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {selectedItems.length > 0 && (
                    <div className="notion-selected-count">
                      {selectedItems.length} {selectedItems.length !== 1 ? (t('items_selected') || 'items selected') : (t('item_selected') || 'item selected')}
                    </div>
                  )}
                </div>
              )}

              <div className="notion-modal-actions">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    color: '#5616ea',
                    borderColor: '#5616ea',
                    '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={importItems}
                  variant="contained"
                  disabled={selectedItems.length === 0 || importing}
                  sx={{
                    backgroundColor: '#5616ea',
                    '&:hover': { backgroundColor: '#4a14d6' },
                    '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
                  }}
                >
                  {importing ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                      {t('importing') || 'Importing...'}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faArrowUpFromBracket} style={{ marginRight: '8px' }} />
                      {t('import_selected') || 'Import Selected'}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <KnowledgeLimitInfoModal
        open={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        onUpgrade={() => { setLimitModalOpen(false); onUpgrade && onUpgrade(); }}
        remainingSlots={remainingSlots}
      />

      <ConfirmImportModal
        open={confirmOpen}
        loading={resolving}
        error={resolveError}
        files={resolvedFiles}
        importing={importing}
        remainingSlots={remainingSlots}
        onClose={() => setConfirmOpen(false)}
        onConfirm={(selected) => { setConfirmOpen(false); doImport(selected); }}
        onUpgrade={() => { setConfirmOpen(false); onUpgrade && onUpgrade(); }}
      />
      {/* Portal-rendered, but a React child of the overlay above (onClick=onClose):
          clicks inside the manage/confirm dialogs would bubble to it and close the
          picker before the delete could run — so stop them here. */}
      <div onClick={(e) => e.stopPropagation()}>
        <ManageConnectionsModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          connections={connections}
          onUpdate={fetchConnections}
          platform="notion"
          connectionType="data"
        />
      </div>
    </div>
  );
}

export default NotionPickerModal;
